import type { TenantScope } from '@onefootprint/request-types/dashboard';
import {
  DecryptOption,
  DecryptOptionToTenantScope,
  decryptOptionFromTenantScope,
} from '../../hooks/use-decrypt-options';
import type { Access } from '../scope-environments';
import { isSandboxOnly, withEnvironments } from '../scope-environments';

type DecryptAccess = Partial<Record<DecryptOption, Access>>;

const accessRank: Record<Access, number> = { none: 0, sandbox_only: 1, sandbox_and_production: 2 };

const widest = (a: Access, b: Access) => (accessRank[a] >= accessRank[b] ? a : b);

const getDecryptAccess = (scopes: TenantScope[]): DecryptAccess => {
  const access: DecryptAccess = {};
  scopes.forEach(scope => {
    const option = decryptOptionFromTenantScope(scope);
    if (option) {
      const scopeAccess = isSandboxOnly(scope) ? 'sandbox_only' : 'sandbox_and_production';
      access[option] = widest(access[option] ?? 'none', scopeAccess);
    }
  });
  return access;
};

const coveringOptions = (option: DecryptOption): DecryptOption[] => {
  if (option === DecryptOption.all) {
    return [];
  }
  // "Everything except PCI data" leaves out card data.
  if (option === DecryptOption.allExceptPciData || option === DecryptOption.card) {
    return [DecryptOption.all];
  }
  if (option === DecryptOption.documents) {
    return [DecryptOption.all, DecryptOption.allExceptPciData, DecryptOption.documentsAndSelfie];
  }
  if (option === DecryptOption.ssn4) {
    return [DecryptOption.all, DecryptOption.allExceptPciData, DecryptOption.usTaxId];
  }
  return [DecryptOption.all, DecryptOption.allExceptPciData];
};

const getCoveringOption = (access: DecryptAccess, option: DecryptOption, minimum: Access) =>
  coveringOptions(option).find(covering => accessRank[access[covering] ?? 'none'] >= accessRank[minimum]);

/** Leaves out options that an "Everything" option already covers. */
const getDecryptScopes = (access: DecryptAccess): TenantScope[] =>
  (Object.entries(access) as [DecryptOption, Access][])
    .filter(([option, optionAccess]) => optionAccess !== 'none' && !getCoveringOption(access, option, optionAccess))
    .map(([option, optionAccess]) => withEnvironments(DecryptOptionToTenantScope[option], optionAccess));

/** Leaves out options that an "Everything" option covers in that environment. */
const getDecryptOptionsByEnvironment = (access: DecryptAccess) => {
  const options = Object.keys(access) as DecryptOption[];
  const decryptedIn = (minimum: Access) =>
    options.filter(
      option =>
        accessRank[access[option] ?? 'none'] >= accessRank[minimum] && !getCoveringOption(access, option, minimum),
    );
  return { sandbox: decryptedIn('sandbox_only'), production: decryptedIn('sandbox_and_production') };
};

export type { DecryptAccess };
export { getCoveringOption, getDecryptAccess, getDecryptOptionsByEnvironment, getDecryptScopes };
